/**
 * Contrato MenuPublicSnapshot — respuesta de GET /api/menu/:id y forma normalizada
 * usada por catálogo, checkout, asistente y fallbacks (menu-public-api, snapshots).
 *
 * @see docs/MENU_PUBLIC_SNAPSHOT_SOLUQR.md
 */

/** Versión del contrato (incrementar si cambian claves obligatorias). */
export const MENU_PUBLIC_SNAPSHOT_VERSION = 1;

/** Claves obligatorias en la respuesta cruda del backend (GET /menu/:id). */
export const MENU_API_TOP_LEVEL_KEYS = [
  'business',
  'categories',
  'products',
  'promos',
  'daily_deal_product',
  'hidden_promo_product',
  'bestseller_product',
];

/** Claves mínimas en `business` para renderizar menú/asistente. */
export const MENU_BUSINESS_REQUIRED_KEYS = ['id', 'name'];

/** B3: no van en GET /menu; el asistente carga GET /menu/:id/assistant-training aparte. */
export const MENU_PUBLIC_BUSINESS_STRIP_KEYS = [
  'assistant_training',
  'mp_oauth_access_token',
  'mp_oauth_refresh_token',
  'mp_oauth_access_token_enc',
  'mp_oauth_refresh_token_enc',
  'whatsapp_cloud_access_token',
  'whatsapp_cloud_access_token_enc',
];

/**
 * @param {object|null|undefined} business
 */
export function sanitizePublicBusinessForMenu(business) {
  if (!business || typeof business !== 'object' || Array.isArray(business)) {
    return {};
  }
  const out = { ...business };
  for (const key of MENU_PUBLIC_BUSINESS_STRIP_KEYS) {
    delete out[key];
  }
  return out;
}

/** Claves mínimas en cada producto listado (catálogo / asistente). */
export const MENU_PRODUCT_REQUIRED_KEYS = ['id', 'name', 'price'];

/**
 * @typedef {object} MenuPublicSnapshot
 * @property {object} business
 * @property {object[]} products
 * @property {object[]} categories
 * @property {object[]} promotions
 * @property {object[]} promos
 * @property {object} settings
 * @property {object|null} daily_deal_product
 * @property {object|null} hidden_promo_product
 * @property {boolean} fallback_mode
 * @property {string|null} fallback_source
 * @property {boolean} fallback_checkout_disabled
 * @property {string|null} fallback_generated_at
 * @property {string|null} source
 * @property {string|null} slug
 */

/**
 * @param {unknown} raw
 * @returns {{ ok: true } | { ok: false, errors: string[] }}
 */
export function validateMenuApiPayload(raw) {
  const errors = [];

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, errors: ['payload must be an object'] };
  }

  for (const key of MENU_API_TOP_LEVEL_KEYS) {
    if (!(key in raw)) {
      errors.push(`missing top-level key: ${key}`);
    }
  }

  const business = raw.business;
  if (!business || typeof business !== 'object' || Array.isArray(business)) {
    errors.push('business must be an object');
  } else {
    for (const key of MENU_BUSINESS_REQUIRED_KEYS) {
      if (business[key] == null || String(business[key]).trim() === '') {
        errors.push(`business.${key} is required`);
      }
    }
  }

  for (const listKey of ['categories', 'products', 'promos']) {
    const value = raw[listKey];
    if (value != null && !Array.isArray(value)) {
      errors.push(`${listKey} must be an array`);
    }
  }

  const products = Array.isArray(raw.products) ? raw.products : [];
  products.slice(0, 5).forEach((product, index) => {
    if (!product || typeof product !== 'object') {
      errors.push(`products[${index}] must be an object`);
      return;
    }
    for (const key of MENU_PRODUCT_REQUIRED_KEYS) {
      if (product[key] == null || (key !== 'price' && String(product[key]).trim() === '')) {
        errors.push(`products[${index}].${key} is required`);
      }
    }
  });

  return errors.length ? { ok: false, errors } : { ok: true };
}

/**
 * @param {unknown} snapshot
 * @returns {{ ok: true } | { ok: false, errors: string[] }}
 */
export function validateMenuPublicSnapshot(snapshot) {
  const apiCheck = validateMenuApiPayload(snapshot);
  if (!apiCheck.ok) return apiCheck;

  const errors = [];
  const s = snapshot;

  if (!Array.isArray(s.promotions)) {
    errors.push('promotions must be an array');
  }
  if (!Array.isArray(s.promos)) {
    errors.push('promos must be an array');
  } else if (Array.isArray(s.promotions) && s.promotions !== s.promos && JSON.stringify(s.promotions) !== JSON.stringify(s.promos)) {
    errors.push('promotions must mirror promos');
  }
  if (typeof s.fallback_mode !== 'boolean') {
    errors.push('fallback_mode must be a boolean');
  }
  if (typeof s.fallback_checkout_disabled !== 'boolean') {
    errors.push('fallback_checkout_disabled must be a boolean');
  }
  if (s.settings == null || typeof s.settings !== 'object' || Array.isArray(s.settings)) {
    errors.push('settings must be an object');
  }

  return errors.length ? { ok: false, errors } : { ok: true };
}

/**
 * Normaliza cualquier payload de menú público (API, menu-public-api o snapshot estático).
 * @param {object|null|undefined} raw
 * @param {string} [slug]
 * @returns {MenuPublicSnapshot}
 */
export function toMenuPublicSnapshot(raw, slug) {
  const promotions = Array.isArray(raw?.promotions)
    ? raw.promotions
    : Array.isArray(raw?.promos)
      ? raw.promos
      : [];

  return {
    business: sanitizePublicBusinessForMenu(raw?.business || {}),
    products: Array.isArray(raw?.products) ? raw.products : [],
    categories: Array.isArray(raw?.categories) ? raw.categories : [],
    promotions,
    promos: promotions,
    settings: raw?.settings || {},
    daily_deal_product: raw?.daily_deal_product ?? null,
    hidden_promo_product: raw?.hidden_promo_product ?? null,
    bestseller_product: raw?.bestseller_product ?? null,
    fallback_mode: Boolean(raw?.fallback_mode),
    fallback_source: raw?.fallback_source || null,
    fallback_checkout_disabled: Boolean(raw?.fallback_checkout_disabled),
    fallback_generated_at: raw?.fallback_generated_at || null,
    source: raw?.source || null,
    slug: slug || raw?.slug || raw?.business?.slug || null,
  };
}

/** Payload de API principal en vivo — nunca queda en modo fallback. */
export function toLiveMenuPublicSnapshot(raw, slug) {
  return {
    ...toMenuPublicSnapshot(raw, slug),
    fallback_mode: false,
    fallback_checkout_disabled: false,
    fallback_source: null,
  };
}

/** Snapshot vacío seguro (tests / degradación). */
export function createEmptyMenuPublicSnapshot(slug = null) {
  return toMenuPublicSnapshot(
    {
      business: { id: slug || '', name: '' },
      categories: [],
      products: [],
      promos: [],
      daily_deal_product: null,
      hidden_promo_product: null,
      bestseller_product: null,
    },
    slug,
  );
}

/** Fixture mínima alineada con GET /api/menu/:id (usada en tests). */
export const MENU_PUBLIC_SNAPSHOT_FIXTURE = {
  business: {
    id: 'cafe-test',
    slug: 'cafe-test',
    name: 'Café Test',
    is_active: true,
    primary_color: '#18181B',
    service_types: ['delivery', 'retiro'],
  },
  categories: [{ id: 'c1', name: 'Bebidas', display_order: 1 }],
  products: [{ id: 'p1', name: 'Latte', price: 3500, availability: 'active' }],
  promos: [{ id: 'promo-1', name: '2x1', is_active: true }],
  daily_deal_product: null,
  hidden_promo_product: null,
  bestseller_product: null,
};
